import { AnyTaggedItem } from "src/types";
import { db } from "../dexie";
import { assertExists } from "../utils/assertExists";
import { Task } from "../types";
import { Tasks } from "../services/Tasks";
import { Sync } from "../sync";
import { Ordering } from "../models/Ordering";
import { updateItem } from "./items";

export async function createTask(task: Task): Promise<void> {
  await Promise.all([
    assertExists(task.parentId, db.tasks, { optional: true }),
    assertExists(task.goalId, db.goals, { optional: true }),
  ]);
  await Sync.upsert(task);
}

export async function deleteTask(id: string): Promise<void> {
  await Sync.delete({ id, type: "task" });
}

export async function getBacklog(): Promise<Task[]> {
  const tasks = await Tasks.getIncomplete();
  return tasks;
}

/** Tasks linked to a goal, in `order` (same sibling key as {@link moveItem} / backlog). */
export async function getTasksForGoal(goalId: string): Promise<Task[]> {
  const tasks = await Tasks.getByGoalId(goalId).toArray();
  return Ordering.toSorted(tasks, (t) => t.order);
}

/** Reorder / reparent tasks when editing a single goal's task subtree (siblings scoped to that goal). */
export async function moveTaskWithinGoal(
  goalId: string,
  item: AnyTaggedItem,
  parent: AnyTaggedItem | null,
  order: number,
): Promise<void> {
  if (item.type !== "task") {
    throw new Error("moveTaskWithinGoal expects a task");
  }
  if (parent && parent.type !== "task") {
    throw new Error("Cannot move item to a parent of a different type");
  }
  if (parent && parent.goalId !== goalId) {
    throw new Error("Parent task must belong to the same goal");
  }
  const parentId = parent ? parent.id : null;
  const siblings = await Tasks.getByParentId(parentId)
    .filter((task) => task.id !== item.id && task.goalId === goalId)
    .toArray();
  Ordering.sortInPlace(siblings, (t) => t.order);
  const newOrder = Ordering.insertAt(siblings, (t) => t.order, order);
  await updateItem({ ...item, order: newOrder, parentId, goalId });
}
